import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";

export const AdministratorVenueEventsMissingData = () => {
    const dispatch = useDispatch();

    const [venueEventsMissingData, setVenueEventsMissingData] = useState([]);
    const [venueEventsMissingDataCount, setVenueEventsMissingDataCount] = useState(0);
    const [skipCount, setSkipCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(1);
    const [areVenueEventsMissingDataLoading, setAreVenueEventsMissingDataLoading] = useState(true);
    const [reportId, setReportId] = useState(undefined);
    
    const UpdateDataCell = (props) => {  
        const content = props.dataItem[props.field];
        const venueEventKey = props.dataItem.venueEventKey;

        return (
            <td className="text-center">
                {content ?? <Button type={"button"} icon="edit" fillMode="link" title="Edit" onClick={() => editField(venueEventKey, props.field)} />}
            </td>
        );
    };

    const ControlCell = (props) => {
        const venueEventKey = props.dataItem.venueEventKey;

        return (
            <td className="text-nowrap">
                <Button themeColor={"primary"} type={"button"} icon="download" title="Fetch" onClick={() => fetchMissingData(venueEventKey)} />
                &nbsp;&nbsp;
                <Button type={"button"} icon="close" title="Hide" onClick={() => hideEvent(venueEventKey)} />
            </td>
        );
    };

    const ViewUrlCell = (props) => {  
        const url = props.dataItem[props.field];     

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const getVenueEventsMissingData = (newPageNumber) => {
        setAreVenueEventsMissingDataLoading(true);

        const dataItem = {
            pageNumber: newPageNumber,
        };
    
        fetch(`/api/administrator/venueeventsmissingdata`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setReportId(result.reportId);
                setItemsPerPage(result.venueEventsMissingDataPerPage);
                setSkipCount(result.venueEventsMissingDataPerPage * (result.pageNumber - 1));
                setVenueEventsMissingDataCount(result.totalVenueEventsMissingDataCount);                
                setVenueEventsMissingData(result.venueEventsMissingData);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);                
            })
            .finally(() => {
                setAreVenueEventsMissingDataLoading(false);
            });
    }

    const editField = (venueEventKey, fieldName) => {
        let value = prompt(`Please enter a value for ${fieldName}`);
        if (!value) {
            return;
        }

        setAreVenueEventsMissingDataLoading(true);

        const dataItem = {
            venueEventKey: venueEventKey,
            key: fieldName,
            value: value,
        };
    
        fetch(`/api/administrator/updatevenueeventmissingdata`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", `Updated the value for ${fieldName}`);     
                
                getVenueEventsMissingData();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);   
                
                setAreVenueEventsMissingDataLoading(false);
            });
    };

    const fetchMissingData = (venueEventKey) => {
        setAreVenueEventsMissingDataLoading(true);

        const dataItem = {
            venueEventKey: venueEventKey,
        };
    
        fetch(`/api/administrator/fetchvenueeventmissingdata`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Fetched the missing data.");     
                
                getVenueEventsMissingData();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);   
                
                setAreVenueEventsMissingDataLoading(false);
            });
    };

    const hideEvent = (venueEventKey) => {
        if (window.confirm("Are you sure you want to hide this event from this report?") === false) {
            return;
        }

        setAreVenueEventsMissingDataLoading(true);

        const dataItem = {
            venueEventKey: venueEventKey,
            reportId: reportId,
        };
    
        fetch(`/api/general/hidevenueeventsfromreport`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Hid event from report");     
                
                getVenueEventsMissingData();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);   
                
                setAreVenueEventsMissingDataLoading(false);
            });
    };

    const pageChange = (event) => {
        const newPageNumber = (event.page.skip / event.page.take) + 1;
        getVenueEventsMissingData(newPageNumber);
    };

    useEffect(() => {
        document.title = "Events Missing Data";

        getVenueEventsMissingData();
    }, []);

    return (
        <>
            {areVenueEventsMissingDataLoading ? <LoadingPanel /> : <></> }

            <Grid
                data={venueEventsMissingData}
                resizable={true}
                skip={skipCount}
                take={itemsPerPage}
                total={venueEventsMissingDataCount}
                pageable={true}
                onPageChange={pageChange}
                scrollable="none"
            >
                <Column cell={ControlCell} />
                <Column field="eventName" title={"Event"} />
                <Column field="venueName" title={"Venue"} />
                <Column field="eventDate" title={"Date"} />
                <Column field="venueEventStatusTypeName" title={"Status"} />
                <Column field="minimumAge" title={"Min Age"} cell={UpdateDataCell} />
                <Column field="purchaseTicketLimit" title={"Purchase Limit"} cell={UpdateDataCell} />
                <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} />
            </Grid>
        </>
    );
}